import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BookOpen, Satellite, Wind, Waves, Cpu, ShieldCheck } from "lucide-react";

function Row({ icon: Icon, label, value, real = true }) {
  return (
    <div className="flex items-start gap-2.5 border-b border-white/5 py-2 last:border-0">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-cyanx" />
      <div className="flex-1">
        <div className="font-mono-tel text-xs font-bold text-slate-200">{label}</div>
        <div className="text-[11px] leading-snug text-slate-400">{value}</div>
      </div>
      <span
        className={`shrink-0 rounded px-1.5 py-0.5 font-mono-tel text-[9px] font-bold ${
          real ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amberx"
        }`}
      >
        {real ? "MEASURED" : "FORECAST"}
      </span>
    </div>
  );
}

export default function MethodologyDialog({ data }) {
  const s = data?.data_sources || {};
  const skill = data?.forecast?.skill || {};
  const detDate = s.detection?.obs_time?.slice(0, 10) || "—";
  const curDate = s.currents?.obs_time?.slice(0, 10) || "—";

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="methodology-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded border border-cyan-500/30 px-2 py-0.5 font-mono-tel text-[10px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/15"
        >
          <BookOpen className="h-3 w-3" /> METHODOLOGY
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[86vh] max-w-2xl overflow-y-auto border-cyan-500/25 bg-[#050f22] text-slate-200">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-extrabold tracking-tight text-white">
            Methodology & Data Provenance
          </DialogTitle>
        </DialogHeader>

        <p className="text-[12px] leading-relaxed text-slate-400">
          Every layer below is retrieved live from public scientific services. Nothing is
          simulated or invented. The seaweed, currents and wind are <span className="text-emerald-300">measured</span>;
          the arrival forecast is a transparent, physically-bounded model built only from those measurements.
        </p>

        <div className="rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="eyebrow mb-1 text-slate-500">Data sources</div>
          <Row icon={Satellite} label="Seaweed detection — USF / NOAA-AOML AFAI (3-day gap-filled)"
            value={`Alternative Floating Algae Index from satellite, via ERDDAP. Uses the 3-day cumulative composite to fill cloud gaps for a stable initial condition. Latest observation: ${detDate}.`} />
          <Row icon={Waves} label="Ocean currents — NOAA-AOML geostrophic + Ekman + Stokes"
            value={`Near-real-time surface current u/v field via ERDDAP, combined with wind-driven Ekman drift and wave (Stokes) drift for the full surface transport of floating rafts. Latest: ${curDate}.`} />
          <Row icon={Wind} label="Wind — NASA POWER"
            value="10 m U/V wind components; drives leeway (~0.7%), Ekman surface drift (~1.2%, deflected 20° right) and Stokes wave drift (~1%)." />
          <Row icon={Satellite} label="Imagery — NASA GIBS MODIS & Copernicus Sentinel-2"
            value="True-colour MODIS and 10 m Sentinel-2 L2A with a Floating-Algae-Index highlight." />
        </div>

        <div className="rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="eyebrow mb-1.5 flex items-center gap-1.5 text-slate-500">
            <Cpu className="h-3.5 w-3.5 text-amberx" /> Prediction algorithm
          </div>
          <ol className="list-decimal space-y-1.5 pl-4 text-[11.5px] leading-snug text-slate-300">
            <li><b>Seed</b> ~700 virtual particles onto the real AFAI mat field, weighted by observed density.</li>
            <li><b>Drift</b> each particle by the full surface transport — NOAA geostrophic current + wind leeway + Ekman drift (deflected ~20° right, N. hemisphere) + Stokes (wave) drift — blended with the motion observed between consecutive satellite days (data assimilation), and <b>hard-capped at 0.7 m/s</b> so it can never move faster than seaweed physically can.</li>
            <li><b>Ensemble</b> of 20 members (perturbed current scaling, windage, turbulent diffusion) run forward 72 h → per-island landfall probability (24/48/72 h), arrival window, and confidence from ensemble agreement. Landfall = a mat reaching within ~6&nbsp;km of the actual shoreline.</li>
            <li><b>Self-validation</b>: the model hindcasts recent days against the later satellite pass and measures skill vs. a persistence baseline.</li>
          </ol>
        </div>

        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
          <div className="mb-1.5 flex items-center gap-1.5 eyebrow text-emerald-300/90">
            <ShieldCheck className="h-3.5 w-3.5" /> Honesty guarantees
          </div>
          <ul className="space-y-1 text-[11.5px] leading-snug text-slate-300">
            <li>• <b>Nowcast is measured</b> (where the belt is now); the <b>arrival ETA is a physical projection</b> from real currents + wind — directional (treat as ±~1 day), not a skill-validated time. Low-confidence islands are shown as a day-range, not a false-precise hour.</li>
            <li>• Forecast drift is bounded to physical seaweed speeds, so an ETA is always consistent with the real mat distance.</li>
            <li>• Belt-drift skill is reported as-is — currently <b className="text-cyanx">
              {skill.available ? `${skill.skill_vs_persistence_pct >= 0 ? "+" : ""}${skill.skill_vs_persistence_pct}% vs persistence (±${skill.position_error_km} km @ ${skill.lead_hours} h)` : "under evaluation"}
            </b>. At basin / multi-day scale a diffuse belt is best modelled as persistent, so this is near zero — we never inflate it, and we do not claim to beat operational bulletins.</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
